import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../useLanguage';
import headshot from '../resources/images/BenHShot_2021_SM.jpg';

interface HeroProps {
  onSectionChange: (section: string) => void;
}

const Hero = ({ onSectionChange }: HeroProps) => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  return (
    <section id="home" className="min-h-screen flex items-center bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-dark-900 dark:via-dark-800 dark:to-dark-900 pt-16 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5 dark:opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8 animate-fade-in">
            <div className="space-y-6">
              <div className="inline-flex items-center px-4 py-2 bg-primary-100 dark:bg-primary-900/30 rounded-full text-primary-700 dark:text-primary-300 text-sm font-medium animate-fade-in">
                {t.hero.availableNow}
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white leading-tight">
                {t.hero.fractionalCTO.split(' ')[0]} <span className="text-primary-500">{t.hero.fractionalCTO.split(' ')[1]}</span> {t.hero.fractionalCTO.split(' ')[2]}
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed max-w-2xl">
                {t.hero.subtitle}
              </p>
              <div className="flex items-center space-x-6 text-sm text-gray-500 dark:text-gray-400">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span>{t.hero.yearsExperience}</span>
                </div>
              </div>
            </div>

            {/* Expertise Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-white dark:bg-dark-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-dark-700 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 animate-slide-up" style={{animationDelay: '0.1s'}}>
                <div className="text-2xl mb-2 text-blue-500">AI</div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">{t.hero.aiml}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{t.hero.aimlDesc}</div>
              </div>
              <div className="bg-white dark:bg-dark-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-dark-700 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 animate-slide-up" style={{animationDelay: '0.2s'}}>
                <div className="text-2xl mb-2 text-orange-500">AWS</div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">{t.hero.cloud}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{t.hero.cloudDesc}</div>
              </div>
              <div className="bg-white dark:bg-dark-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-dark-700 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 animate-slide-up" style={{animationDelay: '0.3s'}}>
                <div className="text-2xl mb-2 text-green-500">PERF</div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">{t.hero.scale}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{t.hero.scaleDesc}</div>
              </div>
              <div className="bg-white dark:bg-dark-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-dark-700 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 animate-slide-up" style={{animationDelay: '0.4s'}}>
                <div className="text-2xl mb-2 text-purple-500">TEAM</div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">{t.hero.teams}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{t.hero.teamsDesc}</div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://calendly.com/benjamin-juarez/60min"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary text-center group"
              >
                <span className="flex items-center justify-center space-x-2">
                  <span>{t.hero.scheduleFree}</span>
                  <span className="group-hover:translate-x-1 transition-transform">→</span>
                </span>
              </a>
              <button
                onClick={() => navigate('/workshops')}
                className="btn-outline text-center"
              >
                {t.header.workshops}
              </button>
            </div>


          </div>

          {/* Image & Stats */}
          <div className="flex flex-col items-center space-y-8 animate-slide-up">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full blur-2xl opacity-20 animate-pulse"></div>
              <img
                src={headshot}
                alt="Ben Juárez - Fractional CTO"
                className="relative w-80 h-80 lg:w-96 lg:h-96 object-cover rounded-full shadow-2xl border-4 border-white dark:border-dark-700"
              />
            </div>
            
            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-6 text-center">
              <div className="space-y-1 animate-fade-in" style={{animationDelay: '0.5s'}}>
                <div className="text-2xl font-bold text-primary-500">9+</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">{t.hero.yearsExp}</div>
              </div>
              <div className="space-y-1 animate-fade-in" style={{animationDelay: '0.6s'}}>
                <div className="text-2xl font-bold text-primary-500">100%</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">{t.hero.clientSatisfaction}</div>
              </div>
              <div className="space-y-1 animate-fade-in" style={{animationDelay: '0.7s'}}>
                <div className="text-2xl font-bold text-primary-500">24h</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">{t.hero.responseTime}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </section>
  );
};

export default Hero;